/**
 * Helpers to convert between scene.content (string) and BlockNote blocks.
 * Supports:
 * - plain text (legacy)
 * - BlockNote JSON
 * - HTML (from RichTextEditor) – interpreted as first paragraph = label, rest = body.
 * - Markdown (from AI workflow, legacy) – detected and converted to HTML.
 */

/** Returns true if content looks like HTML (starts with a tag). */
export function isHtmlContent(content: string | null | undefined): boolean {
    if (!content) return false;
    const trimmed = content.trim();
    return trimmed.startsWith("<") && trimmed.includes(">");
}

/* ── Template alignment helpers ─────────────────────────────────── */

const TEMPLATE_DEFAULT_ALIGNMENT: Record<string, "center" | "start" | null> = {
    A: "center",
    B: "start",
    C: "start",
    D: "start",
    E: "start",
    none: "center",
};

/** Returns the default text alignment for a template, or null if the template has no text layer. */
export function getTemplateAlignment(
    template: string
): "center" | "start" | null {
    return TEMPLATE_DEFAULT_ALIGNMENT[template] ?? "center";
}

/** Returns realistic default content for a freshly-created scene or a template preview.
 * Template A uses a single-focal-point structure (h6 kicker, h1 title, supporting p).
 * Every other template uses a multi-block structure (h6, h2, p, ul, p). Inline text-align
 * is set to the template's default alignment so the result renders correctly without further setup. */
export function getDefaultTemplateContent(
    template: string,
    language: string | null | undefined
): string {
    const align = getTemplateAlignment(template) ?? "start";
    const style = ` style="text-align: ${align}"`;
    const isHe = language === "he";
    if (template === "A") {
        if (isHe) {
            return (
                `<h6${style}>פרק 3 · מבוא</h6>` +
                `<h1${style}>שלושת עקרונות הלמידה האפקטיבית</h1>` +
                `<p${style}>הבנה עמוקה, תרגול מכוון ומשוב מתמשך — שלושה עקרונות שמובילים ללמידה שנשארת.</p>`
            );
        }
        return (
            `<h6${style}>Chapter 3 · Introduction</h6>` +
            `<h1${style}>The three principles of effective learning</h1>` +
            `<p${style}>Deep understanding, deliberate practice, and ongoing feedback — the three principles behind learning that lasts.</p>`
        );
    }
    if (isHe) {
        return (
            `<h6${style}>פרק 3 · מבוא</h6>` +
            `<h2${style}>שלושת עקרונות הלמידה האפקטיבית</h2>` +
            `<p${style}>למידה משמעותית נשענת על שילוב של מספר עקרונות שמחזקים זה את זה ויוצרים זיכרון לטווח ארוך.</p>` +
            `<ul><li${style}>הבנה עמוקה של החומר במקום שינון</li><li${style}>תרגול מכוון עם רמת קושי גוברת</li><li${style}>משוב מתמשך שמכוון את התלמיד</li></ul>`
        );
    }
    return (
        `<h6${style}>Chapter 3 · Introduction</h6>` +
        `<h2${style}>The three principles of effective learning</h2>` +
        `<p${style}>Meaningful learning rests on a combination of principles that reinforce each other and create long-term memory.</p>` +
        `<ul><li${style}>Deep understanding instead of rote memorization</li><li${style}>Deliberate practice with increasing difficulty</li><li${style}>Ongoing feedback that guides the learner</li></ul>`
    );
}

/**
 * Ensures every top-level block (p, h1–h6, li) carries `text-align: align`,
 * adding it where missing (unlike `updateContentAlignment`, which only rewrites
 * existing values). Used so migrated/layout-applied text matches the alignment
 * the old template containers imposed via CSS inheritance. Non-HTML is returned
 * unchanged.
 */
export function setContentAlignment(
    htmlContent: string | null | undefined,
    align: "center" | "start"
): string {
    const raw = (htmlContent ?? "").trim();
    if (!raw || !isHtmlContent(raw)) return raw;
    return raw.replace(
        /<(p|h[1-6]|li)((?:\s[^>]*)?)>/gi,
        (_m, tag: string, attrs: string) => {
            if (/style\s*=/i.test(attrs)) {
                if (/text-align\s*:/i.test(attrs)) {
                    return `<${tag}${attrs.replace(
                        /text-align:\s*[^;"']+/i,
                        `text-align: ${align}`
                    )}>`;
                }
                return `<${tag}${attrs.replace(
                    /style\s*=\s*(["'])/i,
                    `style=$1text-align: ${align}; `
                )}>`;
            }
            return `<${tag}${attrs} style="text-align: ${align}">`;
        }
    );
}

/** Replaces all `text-align` values in inline styles with `newAlignment`. Non-HTML content is returned unchanged. */
export function updateContentAlignment(
    htmlContent: string | null | undefined,
    newAlignment: "center" | "start"
): string {
    const raw = (htmlContent ?? "").trim();
    if (!raw || !isHtmlContent(raw)) return raw;
    return raw.replace(
        /text-align:\s*(center|left|right|start|end|justify)/gi,
        `text-align: ${newAlignment}`
    );
}

const DEFAULT_LABEL = "KEY TAKEAWAY";

/** Extract plain text from blocks for static preview. */
function getTextFromInlineContent(content: unknown): string {
    if (typeof content === "string") return content;
    if (!Array.isArray(content)) return "";
    return (
        content as Array<{ type?: string; text?: string; content?: unknown }>
    )
        .map((item) => {
            if (item.type === "text" && typeof item.text === "string")
                return item.text;
            if (item.type === "link" && Array.isArray(item.content))
                return getTextFromInlineContent(item.content);
            return "";
        })
        .join("");
}

/** Get display text from a block (for preview). */
function getBlockPreviewText(block: {
    content?: unknown;
    children?: unknown[];
}): string {
    const part = getTextFromInlineContent(block.content);
    const childParts = Array.isArray(block.children)
        ? block.children
              .map((c) =>
                  getBlockPreviewText(
                      c as Parameters<typeof getBlockPreviewText>[0]
                  )
              )
              .filter(Boolean)
        : [];
    return [part, ...childParts].filter(Boolean).join(" ");
}

/** Get plain text from blocks array for static display. */
export function blocksToPlainText(blocks: unknown[]): string {
    return (
        blocks
            .map((b) =>
                getBlockPreviewText(
                    b as Parameters<typeof getBlockPreviewText>[0]
                )
            )
            .join("\n")
            .trim() || ""
    );
}

/** Get label (first block text) and body (rest) from blocks for thumbnail/display. */
export function getLabelAndBodyFromBlocks(blocks: unknown[]): {
    label: string;
    body: string;
} {
    const label =
        blocks.length > 0
            ? getBlockPreviewText(
                  blocks[0] as Parameters<typeof getBlockPreviewText>[0]
              ).trim()
            : DEFAULT_LABEL;
    const body =
        blocks.length > 1
            ? blocks
                  .slice(1)
                  .map((b) =>
                      getBlockPreviewText(
                          b as Parameters<typeof getBlockPreviewText>[0]
                      )
                  )
                  .join("\n")
                  .trim()
            : "";
    return { label: label || DEFAULT_LABEL, body };
}

/** Get label (first paragraph) and body (rest) from HTML for thumbnail/display when content is from RichTextEditor. */
export function getLabelAndBodyFromHtml(html: string | null | undefined): {
    label: string;
    body: string;
} {
    const raw = (html ?? "").trim();
    if (!raw.startsWith("<")) return { label: DEFAULT_LABEL, body: raw };
    const firstMatch = raw.match(/<(?:p|h6)[^>]*>([\s\S]*?)<\/(?:p|h6)>/i);
    const label = firstMatch
        ? firstMatch[1]
              .replace(/<[^>]+>/g, "")
              .replace(/\s+/g, " ")
              .trim()
        : DEFAULT_LABEL;
    const closingTag = firstMatch?.[0].includes("</h6>") ? "</h6>" : "</p>";
    const restHtml = firstMatch
        ? raw.slice(raw.indexOf(closingTag) + closingTag.length).trim()
        : raw;
    const body = restHtml
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return { label: label || DEFAULT_LABEL, body };
}

/** Match a single trailing empty paragraph (optional attributes, only whitespace/br inside). */
const TRAILING_EMPTY_P = /<p(?:\s[^>]*)?>(\s|&nbsp;|<br\s*\/?>)*<\/p>\s*$/i;

/** Remove only *redundant* trailing empty paragraphs (3+ at the end).
 * Keeps at least 2 blocks (label + body) and allows two trailing empty so the user can have
 * multiple content lines and create new ones with Enter (one empty for current line, one for next). */
export function stripTrailingEmptyParagraphs(
    html: string | null | undefined
): string {
    let result = (html ?? "").trim();
    if (!result.startsWith("<")) return result;
    const blockCount = (result.match(/<(p|h[1-6])(\s|>)/gi) || []).length;
    if (blockCount <= 2) return result;
    /* Strip one trailing empty at a time; stop when we have at most two trailing empty (so Enter works). */
    for (;;) {
        if (!TRAILING_EMPTY_P.test(result)) return result;
        const next = result.replace(TRAILING_EMPTY_P, "").trim();
        if (next === result) return result;
        const nextBlockCount = (next.match(/<(p|h[1-6])(\s|>)/gi) || []).length;
        if (nextBlockCount <= 2) return result;
        /* Only strip if we still have 2+ trailing empty after this (i.e. we had 3+ trailing empty). */
        const afterTwo = next.replace(TRAILING_EMPTY_P, "").trim();
        if (!TRAILING_EMPTY_P.test(afterTwo)) return result;
        result = next;
    }
}
